import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Bell, Settings as Cog, Clock, Users, Check, CalendarDays, ShoppingCart, Refrigerator, LifeBuoy, Sparkles, ChevronRight, AlertTriangle, Moon, Undo2 } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import BottomNav from "../components/BottomNav";
import { SpeechBubble, MillieHero } from "../components/MascotBubble";
import { moodForHour } from "../lib/millie";
import DishImage from "../components/DishImage";
import DealsNudge from "../components/DealsNudge";
import RecapSheet, { useRecap } from "../components/RecapSheet";
import FridgeNudge from "../components/FridgeNudge";
import SideSuggestion from "../components/SideSuggestion";
import { LeftoversCard } from "../components/Leftovers";

const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

export default function Home() {
  const { user, activeId, activeHousehold } = useAuth();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [shop, setShop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gen, setGen] = useState(false);
  const today = DAY_NAMES[new Date().getDay()];
  const cur = activeHousehold?.currency || "A$";
  const { recap, generate: generateRecap, markRead } = useRecap(activeId);
  const [recapOpen, setRecapOpen] = useState(false);
  const [notes, setNotes] = useState([]);
  useEffect(() => { api.get("/notifications").then(({ data }) => setNotes(data)).catch(() => {}); }, [activeId]);
  const unread = (recap && !recap.read) || notes.some((n) => !n.read);
  const readNotes = async () => { setNotes((p) => p.map((n) => ({ ...n, read: true }))); try { await api.post("/notifications/read"); } catch {} };
  const isRecapTime = new Date().getDay() === 0 ? new Date().getHours() >= 12 : new Date().getDay() === 1;

  const load = useCallback(async () => {
    if (!activeId) return;
    setLoading(true);
    try {
      const [p, s] = await Promise.all([api.get(`/plan/${activeId}`), api.get(`/shopping/${activeId}`)]);
      setPlan(p.data);
      setShop(s.data);
    } finally { setLoading(false); }
  }, [activeId]);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setGen(true);
    toast.loading("Millie is planning your week…", { id: "gen" });
    try {
      const { data } = await api.post("/plan/generate", { household_id: activeId });
      setPlan(data);
      const s = await api.get(`/shopping/${activeId}`);
      setShop(s.data);
      toast.success("Your week is sorted!", { id: "gen" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail), { id: "gen" });
    } finally { setGen(false); }
  };

  const todayPlan = plan?.days?.find((d) => d.day === today);
  const [nightOffBusy, setNightOffBusy] = useState(false);
  const nightOff = async (undo = false) => {
    setNightOffBusy(true);
    try {
      const { data } = await api.post("/plan/night-off", { household_id: activeId, day: today, undo });
      setPlan(data);
      toast.success(undo ? "Back on — I've got dinner covered." : "Night off it is. No guilt, no dishes from me.");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setNightOffBusy(false); }
  };
  const firstName = (user?.name || "there").split(" ")[0];
  const hour = new Date().getHours();
  const greet = hour < 12 ? "Morning" : hour < 18 ? "Afternoon" : "Evening";
  const invCount = activeHousehold?.inventory?.length || 0;
  const shopCount = shop?.categories?.reduce((s, c) => s + c.items.length, 0) || 0;

  return (
    <div className="min-h-screen w-full bg-[#F7E9E6] flex justify-center">
      <div className="relative w-full max-w-md min-h-screen bg-gradient-to-b from-[#FFF6F1] to-[#FCE7EC] overflow-hidden pb-28">
        {/* Header */}
        <div className="px-6 pt-12 flex items-start justify-between">
          <div>
            <h1 className="font-logo text-4xl text-sorted-ink leading-none">sorted<span className="text-sorted-rose">.</span></h1>
            <p className="text-sorted-slate font-semibold text-sm mt-1">Your household. Sorted.</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setRecapOpen(true)} data-testid="home-bell" className="relative w-11 h-11 rounded-full bg-white/80 border-2 border-white flex items-center justify-center text-sorted-ink shadow-soft">
              <Bell size={19} strokeWidth={2.5} />
              {unread && <span className="absolute top-2 right-2 w-2.5 h-2.5 rounded-full bg-sorted-rose border-2 border-white" data-testid="home-bell-dot" />}
            </button>
            <button onClick={() => navigate("/settings")} data-testid="home-settings" className="w-11 h-11 rounded-full bg-white/80 border-2 border-white flex items-center justify-center text-sorted-ink shadow-soft">
              <Cog size={19} strokeWidth={2.5} />
            </button>
          </div>
        </div>

        {/* Millie hero */}
        <div className="relative mt-2 h-56">
          <MillieHero mood={plan ? moodForHour() : "idea"} className="absolute right-0 bottom-0 h-56 w-[46%]" testId="home-millie-hero" />
          <div className="absolute left-6 top-4 w-[50%]">
            <SpeechBubble tail="br" testId="home-millie-bubble">
              {greet}, {firstName}! {todayPlan ? "Your week's sorted. Let's make today a good one!" : "Let's get your week sorted — too easy."}
            </SpeechBubble>
          </div>
        </div>

        {(recap && !recap.read) || (isRecapTime && recap) ? (
          <div className="px-6 mt-2">
            <button onClick={() => setRecapOpen(true)} data-testid="home-recap-card"
              className="w-full bg-[#FCEFCF] rounded-3xl border-2 border-white/60 p-4 text-left shadow-soft flex items-center gap-3">
              <span className="text-3xl">{recap.emoji}</span>
              <span className="flex-1 min-w-0">
                <span className="font-heading font-semibold text-sorted-ink block leading-tight">{recap.headline}</span>
                <span className="text-xs text-sorted-slate font-semibold">Sunday arvo recap · {recap.total_wins} little wins this week</span>
              </span>
              <ChevronRight size={18} strokeWidth={2.5} className="text-sorted-slate" />
            </button>
          </div>
        ) : null}

        {/* Tonight's dinner */}
        <div className="px-6 mt-2">
          <div className="flex items-end justify-between mb-2">
            <h2 className="font-hand text-3xl text-sorted-ink">Tonight's dinner</h2>
            {todayPlan && (
              <button onClick={() => navigate("/meals")} data-testid="home-view-recipe" className="text-sm font-bold text-sorted-rose flex items-center gap-0.5">
                View recipe <ChevronRight size={15} strokeWidth={2.5} />
              </button>
            )}
          </div>

          {loading ? (
            <div className="h-44 rounded-3xl bg-white/60 animate-pulse" />
          ) : plan && !plan.days?.find((d) => d.day === today) ? (
            <div className="bg-white rounded-3xl shadow-soft border-2 border-[#FDECEF] p-5 text-center" data-testid="home-night-off">
              <p className="font-heading text-lg font-semibold text-sorted-ink">Night off tonight 🙌</p>
              <p className="text-sm text-sorted-slate mt-1">No dinner planned for {today} — leftovers, takeaway or a cheeky SOS?</p>
              <button onClick={() => navigate("/sos")} className="mt-3 bg-sorted-rose text-white text-sm font-bold px-4 py-2 rounded-full">SOS Dinner</button>
            </div>
          ) : todayPlan?.night_off ? (
            <div className="bg-white rounded-3xl shadow-soft border-2 border-[#FDECEF] p-5 text-center" data-testid="home-night-off-taken">
              <Moon size={26} strokeWidth={2.5} className="mx-auto text-sorted-rose" />
              <p className="font-heading text-lg font-semibold text-sorted-ink mt-1">Millie's got the night off 🌙</p>
              <p className="text-sm text-sorted-slate mt-1"><b>{todayPlan.meal.title}</b> is parked — leftovers, takeaway or whatever's easy. I've left it off your shopping list.</p>
              <div className="mt-3 flex gap-2 justify-center">
                <button onClick={() => nightOff(true)} disabled={nightOffBusy} data-testid="home-night-off-undo" className="bg-sorted-rose text-white text-sm font-bold px-4 py-2 rounded-full flex items-center gap-1 disabled:opacity-60"><Undo2 size={14} strokeWidth={2.5} /> Back on, Millie</button>
                <button onClick={() => navigate("/sos")} className="bg-white border-2 border-[#FDECEF] text-sorted-ink text-sm font-bold px-4 py-2 rounded-full">SOS Dinner</button>
              </div>
            </div>
          ) : todayPlan ? (
            <div className="bg-white rounded-3xl shadow-soft border-2 border-[#FDECEF] p-3" data-testid="home-tonight-card">
              <div className="flex gap-3">
                <DishImage meal={todayPlan.meal} className="w-28 h-28 rounded-2xl shrink-0" testId="home-dish-image" />
                <div className="flex-1 min-w-0">
                  <h3 className="font-heading text-lg font-semibold text-sorted-ink leading-tight" data-testid="home-tonight-title">{todayPlan.meal.title}</h3>
                  <div className="flex items-center gap-3 mt-1 text-xs font-bold text-sorted-slate">
                    <span className="flex items-center gap-1"><Clock size={13} strokeWidth={2.5} />{todayPlan.meal.prep_time_min} min</span>
                    <span className="flex items-center gap-1"><Users size={13} strokeWidth={2.5} />Serves {todayPlan.meal.servings}</span>
                  </div>
                  {todayPlan.meal.uses_pantry && (
                    <p className="flex items-center gap-1.5 mt-2 text-xs font-bold text-emerald-600">
                      <span className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center"><Check size={12} strokeWidth={3} /></span>
                      You've already got everything you need — ripper!
                    </p>
                  )}
                </div>
              </div>
              <button onClick={() => navigate("/cook/today")} data-testid="home-start-cooking"
                className="mt-3 w-full bg-sorted-rose hover:bg-sorted-roseHover text-white font-bold py-3 rounded-full flex items-center justify-center gap-2 transition-colors">
                Start cooking <ChevronRight size={16} strokeWidth={2.5} />
              </button>
              {activeHousehold?.night_off_days?.includes(today) && (
                <p className="mt-2 text-[11px] font-bold text-sorted-slate bg-[#FFF6F1] rounded-2xl px-3 py-2" data-testid="home-night-off-hint">You usually take {today}s off — I kept tonight light. Want the night off?</p>
              )}
              <button onClick={() => nightOff(false)} disabled={nightOffBusy} data-testid="home-night-off"
                className="mt-2 w-full text-sm font-bold text-sorted-slate py-2 rounded-full flex items-center justify-center gap-1.5 hover:text-sorted-ink disabled:opacity-60">
                <Moon size={15} strokeWidth={2.5} /> Take the night off, Millie
              </button>
            </div>
          ) : (
            <button onClick={generate} disabled={gen} data-testid="home-generate"
              className="w-full bg-white border-2 border-dashed border-sorted-blush rounded-3xl p-8 flex flex-col items-center gap-2 text-sorted-ink shadow-soft">
              <Sparkles size={30} strokeWidth={2.5} className="text-sorted-rose" />
              <span className="font-heading text-lg font-semibold">{gen ? "Sorting…" : "Plan my week"}</span>
              <span className="text-sm text-sorted-slate">Millie builds {activeHousehold?.meals_per_week || 7} dinners around your fam</span>
            </button>
          )}

          {todayPlan && <SideSuggestion activeId={activeId} day={todayPlan.day} meal={todayPlan.meal} onUpdated={setPlan} />}
        </div>

        <div className="px-6 mt-4 space-y-4">
          <LeftoversCard activeId={activeId} />
          <FridgeNudge activeId={activeId} onSwapped={(p) => { setPlan(p); }} />
        </div>

        {plan && (
          <div className="px-6 mt-4">
            <DealsNudge activeId={activeId} hasPlan={!!plan} shops={activeHousehold?.supermarkets || []} />
          </div>
        )}

        {/* Quick tiles */}
        <div className="px-6 mt-5 grid grid-cols-2 gap-3">
          <Tile testId="tile-week" onClick={() => navigate("/meals")} icon={CalendarDays} bg="bg-[#FCE1E8]" title="Your week" sub={plan ? `${plan.days.length} dinners planned${plan.lunches?.length ? " + lunches" : ""}${plan.breakfasts?.length ? " + brekkie" : ""}` : "Not planned yet"} />
          <Tile testId="tile-shop" onClick={() => navigate("/shopping")} icon={ShoppingCart} bg="bg-[#DDEBE1]" title="Shopping"
            sub={shop ? `${shopCount} items · ${cur}${Number(shop.estimated_total).toFixed(0)}/${cur}${activeHousehold?.budget_weekly}` : "—"}
            progress={shop ? Math.min(100, (Number(shop.estimated_total) / (activeHousehold?.budget_weekly || 1)) * 100) : 0} />
          <Tile testId="tile-kitchen" onClick={() => navigate("/kitchen")} icon={Refrigerator} bg="bg-[#FBE6DC]" title="Your kitchen" sub={`${invCount} items at home`}
            warn={invCount ? `${Math.min(3, invCount)} to use soon` : null} />
          <Tile testId="tile-sos" onClick={() => navigate("/sos")} icon={LifeBuoy} bg="bg-[#FAD9E1]" title="SOS Dinner" sub="Millie, I need help!" />
        </div>
      </div>
      <RecapSheet open={recapOpen} onClose={() => setRecapOpen(false)} recap={recap} generate={generateRecap} markRead={markRead} notifications={notes} onReadNotifications={readNotes} />
      <BottomNav />
    </div>
  );
}

function Tile({ onClick, icon: Icon, bg, title, sub, progress, warn, testId }) {
  return (
    <motion.button whileTap={{ scale: 0.97 }} onClick={onClick} data-testid={testId}
      className={`${bg} rounded-3xl p-4 text-left shadow-soft border-2 border-white/60`}>
      <span className="w-10 h-10 rounded-2xl bg-white/70 flex items-center justify-center text-sorted-ink mb-2">
        <Icon size={20} strokeWidth={2.5} />
      </span>
      <p className="font-heading font-semibold text-sorted-ink leading-tight">{title}</p>
      <p className="text-xs font-semibold text-sorted-slate mt-0.5">{sub}</p>
      {typeof progress === "number" && (
        <div className="w-full h-1.5 bg-white/70 rounded-full mt-2 overflow-hidden">
          <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${progress}%` }} />
        </div>
      )}
      {warn && <p className="flex items-center gap-1 text-[11px] font-bold text-amber-600 mt-1"><AlertTriangle size={11} strokeWidth={2.5} />{warn}</p>}
    </motion.button>
  );
}
